<script lang="ts">
	import { ButtonV2, CircleAvatar, Spinner, Text } from '@Shelly/shelly-design-system/index';
	import { createEventDispatcher } from 'svelte';
	import { format } from 'svelte-i18n';
	import ZoomableContainer from './ZoomableContainer.svelte';
	import VideoLoader from './VideoLoader.svelte';
	import type { VideoContainerState } from '../types/video-container';

	const dispatch = createEventDispatcher();

	// Single source of truth for the component's visual state.
	// Consumers compute and pass the full state; VideoContainer only renders it.
	export let state: VideoContainerState = null;

	export let isZoomable: boolean = true;
	export let isZoomed: boolean = false;
	export let hasZoomCtrlShortcut: boolean = false;
	export let compact: boolean = false;

	// CircleAvatar size for blocked-state placeholders.
	// Consumers set this based on their layout size (e.g. tile size).
	export let avatarSize: 's' | 'm' | 'm_plus' | 'l' | 'xl' | 'xxl' = 'l';

	// Offline state display props
	export let previewImage: string = '';
	export let previewImageFit: 'cover' | 'contain' = 'cover';
	export let lastSeenTime: string = '';
	/** Suppress heading/avatar in blocked states — parent card shows status chrome. */
	export let minimalBlockedState = false;
	/** Fill the parent cell (viewport-fit mosaics) instead of locking to 16:9. */
	export let fillContainer = false;

	// Derived state
	$: isBlockedState = state?.type !== 'loading' && !!state?.type;
	$: hasPreviewImage = state?.type === 'offline' && !!previewImage;
	$: isStartingState = state?.type === 'starting';
</script>

<div class="video-container-wrapper" class:fill-container={fillContainer}>
	<div class="video-player-area">
		{#if isBlockedState}
			<!-- Privacy/Offline/Error/Starting: show placeholder, do NOT render slot (WebRTCPlayer won't mount) -->
			<div
				class="state-placeholder"
				class:has-preview={hasPreviewImage}
				class:compact
				class:fill-container={fillContainer}
			>
				{#if hasPreviewImage}
					<img
						src={previewImage}
						alt={state?.heading ||
							$format('camera_preview_alt', { default: 'Camera preview' })}
						class="offline-preview-image"
						style={`object-fit: ${previewImageFit};`}
					/>
				{/if}

				<div class="state-content">
					{#if isStartingState}
						<Spinner size={compact ? 's' : 'm'} />
					{:else if !hasPreviewImage && !minimalBlockedState}
						<CircleAvatar
							icon={state?.icon || 'fal fa-video-slash'}
							size={avatarSize}
							disabled
						/>
					{/if}
					{#if !minimalBlockedState && (state?.type === 'offline' || !compact || !hasPreviewImage)}
						<Text
							styleType={compact ? 'body-small' : 'body'}
							class="{hasPreviewImage ? '' : compact ? 'mt-xs' : 'mt-m'} text-center"
						>
							{state?.heading ||
								$format('camera_unavailable', { default: 'Camera unavailable' })}
						</Text>
					{/if}
					{#if state?.message && !compact && !minimalBlockedState}
						<Text styleType="body-small" class="text-color-muted mt-xs text-center">
							{state.message}
						</Text>
					{/if}
					{#if lastSeenTime && state?.type === 'offline' && !minimalBlockedState}
						<Text
							styleType={compact ? 'tiny' : 'body-small'}
							class="text-color-muted mt-xxs text-center"
						>
							{lastSeenTime}
						</Text>
					{/if}
					{#if state?.cta && !compact}
						<ButtonV2
							variant="secondary"
							size="s"
							class="mt-s"
							on:click={() => dispatch('retry')}
						>
							{state.cta.title}
						</ButtonV2>
					{/if}
				</div>
			</div>
		{:else}
			<!-- Loading/Normal: render slot with overlays on top -->
			{#if state?.type === 'loading'}
				<VideoLoader text={state.message} />
			{/if}

			<ZoomableContainer
				disabled={!isZoomable || state?.type === 'loading'}
				bind:isZoomed
				requireCtrlForWheel={hasZoomCtrlShortcut}
			>
				<slot />
			</ZoomableContainer>
		{/if}

		<div class="video-overlay-top-left">
			<slot name="topLeft" />
		</div>
	</div>
	<slot name="actions" />
</div>

<style>
	.video-container-wrapper {
		display: flex;
		flex-direction: column;
		width: 100%;
		border-radius: var(--radius-big);
		overflow: hidden;
	}

	.video-container-wrapper.fill-container {
		height: 100%;
		min-height: 0;
	}

	.video-player-area {
		position: relative;
		background-color: var(--color-2);
	}

	.video-container-wrapper.fill-container .video-player-area {
		flex: 1;
		min-height: 0;
		height: 100%;
	}

	.video-player-area :global(video) {
		display: block;
		width: 100%;
		aspect-ratio: 16 / 9;
	}

	.video-container-wrapper.fill-container .video-player-area :global(video) {
		position: absolute;
		inset: 0;
		width: 100%;
		height: 100%;
		aspect-ratio: unset;
	}

	.state-placeholder {
		position: relative;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		padding: var(--spacing-s, 8px);
		width: 100%;
		aspect-ratio: 16 / 9;
		background-color: var(--color-1);
	}

	.state-placeholder.fill-container {
		aspect-ratio: unset;
		height: 100%;
		min-height: 0;
	}

	.state-placeholder.compact {
		padding: var(--spacing-xs, 4px);
	}

	.state-placeholder.has-preview {
		justify-content: flex-end;
	}

	.offline-preview-image {
		position: absolute;
		inset: 0;
		width: 100%;
		height: 100%;
		background-color: var(--color-0);
		filter: grayscale(100%);
		opacity: 0.4;
	}

	.state-content {
		position: relative;
		z-index: 3;
		display: flex;
		flex-direction: column;
		align-items: center;
	}

	.video-overlay-top-left {
		position: absolute;
		top: var(--spacing-s, 8px);
		left: var(--spacing-s, 8px);
		pointer-events: none;
		z-index: 1;
	}

	.video-overlay-top-left :global(*) {
		pointer-events: auto;
	}
</style>
